"use client";

import { c as _c } from "react/compiler-runtime";
import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { getInsuranceDecisionCoverage } from "@/configuration/groups/tpr/workflows/insurance-decision-coverage";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import { useConfirmInsuranceNetworkMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { receivingAssignmentOutputSchema } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
export function TprNetworkConfirmationButton(t0) {
  const $ = _c(30);
  const {
    task,
    steps,
    coverage,
    requirements
  } = t0;
  const mutation = useConfirmInsuranceNetworkMutation();
  const invalidate = useInvalidateServerComponent("getReferralInstructions");
  let t1;
  if ($[0] !== steps) {
    const assignmentStep = steps.find(_temp);
    t1 = receivingAssignmentOutputSchema.safeParse(assignmentStep?.output);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsed = t1;
  const assignment = parsed.success ? parsed.data : null;
  let t2;
  if ($[2] !== coverage) {
    t2 = getInsuranceDecisionCoverage(coverage);
    $[2] = coverage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const policy = t2;
  if (!task.patient?.id || !assignment?.providerNpi || !assignment.targetDepartmentId || assignment.scope === "manual_review" || policy.selfPay || task.status === TaskStatus.PROCESSING) {
    return null;
  }
  if (requirements.networkVerification) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className="text-sm text-muted-foreground">In-network status confirmed by Ops for this insurance and provider.</p>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (requirements.clinicNetwork === "in_network" && requirements.providerNetwork === "in_network") {
    return null;
  }
  const variantKey = requirements.networkVariantKey;
  if (variantKey === null || variantKey === undefined) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className="text-sm">Identify the insurance plan variant before confirming in-network status.</p>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== assignment.providerNpi || $[7] !== assignment.targetDepartmentId || $[8] !== policy.insuranceId || $[9] !== policy.insurancePackageId || $[10] !== task.patient.id || $[11] !== variantKey) {
    t3 = {
      expectedVariantKey: variantKey,
      patientId: task.patient.id,
      insuranceId: policy.insuranceId,
      packageNumber: policy.insurancePackageId,
      providerNpi: assignment.providerNpi,
      departmentId: assignment.targetDepartmentId
    };
    $[6] = assignment.providerNpi;
    $[7] = assignment.targetDepartmentId;
    $[8] = policy.insuranceId;
    $[9] = policy.insurancePackageId;
    $[10] = task.patient.id;
    $[11] = variantKey;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const scope = t3;
  let t4;
  if ($[13] !== invalidate || $[14] !== mutation || $[15] !== scope || $[16] !== task.displayId || $[17] !== task.id) {
    t4 = async () => {
      ;
      try {
        await mutation.mutateAsync({
          taskId: task.id,
          displayId: task.displayId,
          scope
        });
        refreshTprWorkflowProgress(invalidate);
        toast({
          title: "In-network status confirmed"
        });
      } catch (t5) {
        const error = t5;
        toast({
          title: "Unable to save network confirmation",
          description: error instanceof Error ? error.message : "Refresh and try again.",
          variant: "destructive"
        });
      }
    };
    $[13] = invalidate;
    $[14] = mutation;
    $[15] = scope;
    $[16] = task.displayId;
    $[17] = task.id;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const confirm = t4;
  let t5;
  if ($[19] !== assignment.providerFamilyName || $[20] !== assignment.providerGivenName || $[21] !== policy.insurancePackageId) {
    t5 = <p className="text-sm">After verifying that the clinic and {assignment.providerGivenName} {assignment.providerFamilyName} accept this patient's plan (package {policy.insurancePackageId}), confirm below. This also applies to future documents and referrals for this patient while the insurance policy, provider, and clinic stay the same.</p>;
    $[19] = assignment.providerFamilyName;
    $[20] = assignment.providerGivenName;
    $[21] = policy.insurancePackageId;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const t6 = mutation.isPending ? "Saving..." : "Confirm in-network";
  let t7;
  if ($[23] !== confirm || $[24] !== mutation.isPending || $[25] !== t6) {
    t7 = <Button size="sm" onClick={confirm} disabled={mutation.isPending}>{t6}</Button>;
    $[23] = confirm;
    $[24] = mutation.isPending;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t5 || $[28] !== t7) {
    t8 = <div className="space-y-2">{t5}{t7}</div>;
    $[27] = t5;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function _temp(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT && step.status === TaskStepStatus.COMPLETED;
}